import { universalFields } from '../_shared/universal.js';
import { buyBoxBlockSchemas, detailsBlockSchema } from '../_shared/buyBoxBlocks.js';
/**
 * buy-box — THE BUY BOX ON ITS OWN, without the product section around it (founder, 25/10/2026).
 *
 * Until now the only way to get a buy box was to insert `main-product`, the two-column product section:
 * gallery on the left, the buy-box column on the right, and a product taken from the ROUTE. That is the
 * right shape for a product page and the wrong one for the sellers who work in LANDING PAGES — they build
 * their own page, write their own copy, bring their own photographs, and all they want from us is the box
 * that takes the order. They were inserting a whole product section to get at one of its columns, and on a
 * page with no product route it rendered the house sample in the editor and then NOTHING on the live shop
 * (`resolveProduct` hides a product section that is bound to nothing — see `_shared/product`).
 *
 * So this section is the buy-box column, alone:
 *  - it hosts the SAME blocks (`buyBoxBlockSchemas`, the shared set), in the SAME `details` static child,
 *    so the pieces, their settings and the storefront island are literally the same code;
 *  - it takes its product from its OWN `product` picker, and from the route when it happens to sit on a
 *    product template (see `resolveBuyboxProduct`) — so the same section works on both kinds of page;
 *  - it carries the DESIGN the two-column layout used to supply for free: where the box sits, how wide it
 *    is, whether its text is centred, and whether it is painted on a card (see `frame.ts`).
 */
export const buyBoxSchema = {
    type: 'buy-box',
    name: 'Buy box',
    version: 1,
    // Every page type a seller builds a funnel on. `main-product` stays product-only: it IS the product
    // page. This one is the piece you drop anywhere.
    templates: ['index', 'page', 'collection', 'collections', 'product', 'thank-you'],
    fields: [
        {
            id: 'product',
            type: 'product-picker',
            label: 'Product',
            group: 'Product',
            info: 'The product this box sells. On a product page the page’s own product wins, so one buy box works on both.',
        },
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a button higher up the page can scroll down to it.',
            default: false,
        },
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            id: 'alignment',
            type: 'select',
            label: 'Box position',
            group: 'Layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
            info: 'Where the box sits when it is narrower than the page.',
        },
        {
            id: 'boxWidth',
            type: 'range',
            label: 'Box width',
            group: 'Layout',
            default: 560,
            min: 280,
            max: 1200,
            step: 20,
            unit: 'px',
        },
        {
            id: 'textAlign',
            type: 'select',
            label: 'Text alignment',
            group: 'Layout',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
            info: 'Aligns the title, the price and the benefit lines inside the box.',
        },
        {
            id: 'sticky',
            type: 'checkbox',
            label: 'Keep in view while scrolling',
            group: 'Layout',
            default: false,
            info: 'Desktop only. The box follows the reader down a long landing page; on phones it never pins.',
        },
        // ── Card ─────────────────────────────────────────────────────────────────
        {
            id: 'cardStyle',
            type: 'select',
            label: 'Card style',
            group: 'Card',
            default: 'none',
            options: [
                { value: 'none', label: 'No card' },
                { value: 'card', label: 'On a card' },
            ],
            info: 'Paints a surface under the box so it stands out from the page behind it.',
        },
        {
            id: 'cardBackground',
            type: 'color',
            label: 'Card background',
            group: 'Card',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'cardStyle', equals: 'card' },
        },
        {
            id: 'cardPadding',
            type: 'range',
            label: 'Card padding',
            group: 'Card',
            default: 24,
            min: 0,
            max: 64,
            step: 4,
            unit: 'px',
            visibleWhen: { field: 'cardStyle', equals: 'card' },
        },
        {
            id: 'cardRadius',
            type: 'range',
            label: 'Card corners',
            group: 'Card',
            default: 16,
            min: 0,
            max: 48,
            step: 2,
            unit: 'px',
            visibleWhen: { field: 'cardStyle', equals: 'card' },
        },
        {
            id: 'showBorder',
            type: 'checkbox',
            label: 'Card border',
            group: 'Card',
            default: false,
            visibleWhen: { field: 'cardStyle', equals: 'card' },
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border color',
            group: 'Card',
            default: { ref: 'custom', value: '#e5e7eb' },
            visibleWhen: { field: 'showBorder', equals: true },
        },
        {
            id: 'shadow',
            type: 'select',
            label: 'Card shadow',
            group: 'Card',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'soft', label: 'Soft' },
                { value: 'strong', label: 'Strong' },
            ],
            visibleWhen: { field: 'cardStyle', equals: 'card' },
        },
        ...universalFields,
    ],
    // ONE static child, the same `details` container `main-product` uses — which is also what lets the
    // storefront's main-product island hydrate this section with no second hydrator (it finds its blocks by
    // looking the `details` child up by type).
    accepts: ['details'],
    staticChildren: [{ type: 'details', label: 'Buy box' }],
    blocks: [detailsBlockSchema, ...buyBoxBlockSchemas],
    maxBlocks: 40,
};
/**
 * The default stack is the COD funnel, minus everything a landing page already says itself.
 *
 * No description block and no collapsible row: the seller wrote those above the box in her own words — that
 * is what a landing page IS. What is left is the part only we can draw: the title the order will carry, the
 * price, the variant picker, and the `cod-form` block (« Boîte d'achat » in the editor) which collects the
 * buyer's details AND places the order in one go.
 */
export const buyBoxPresets = [
    {
        name: 'Buy box',
        settings: {
            alignment: 'center',
            boxWidth: 560,
            textAlign: 'left',
            cardStyle: 'none',
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            width: 'standard',
        },
        blocks: [
            {
                type: 'details',
                static: true,
                blocks: [
                    { type: 'title' },
                    { type: 'price' },
                    { type: 'variant-picker', settings: { variantStyle: 'auto' } },
                    { type: 'cod-form', settings: { formTitle: '@t:cod.form_title' } },
                ],
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon. It lives beside the schema (not in
 * `index.ts`) so `registry.lazy.gen.ts` can read it without pulling the render code in.
 */
export const buyBoxMeta = {
    label: 'Buy box',
    group: 'Product',
    icon: 'cash',
};
//# sourceMappingURL=schema.js.map